import React, { useMemo } from "react";
import { Table, Headings, Paragraph, Tooltip } from "@sentara-health/optimahealth_web";
import {
    formatMedicalText,
    useExtractServiceTypeSummary,
    formatCellValue,
    DeviceTypes,
    useDeviceType,
} from "common";
import { ClaimResponse } from "@sentara-health/sentara-api-hooks-core";
import { useTranslation } from "react-i18next";

interface ClaimsServiceTypeTableProps {
    claimData: ClaimResponse;
}

interface ServiceSummary {
    charged: number;
    amountNotCovered: number;
    amountAllowed: number;
    deductible: number;
    copay: number;
    coinsurance: number;
    copayCoinsurance: number;
    paidByPlan: number;
    patientResponsibility: number;
    notes?: string;
    noteDisplay?: string;
    serviceType: string;
}

interface RowLabel {
    label: string;
    key: keyof ServiceSummary;
}

const ClaimsServiceTypeTable: React.FC<ClaimsServiceTypeTableProps> = ({ claimData }) => {

    const { t } = useTranslation();
    const deviceType = useDeviceType();
    const isMobile = deviceType === DeviceTypes.MOBILE;
    const serviceSummary = useExtractServiceTypeSummary(claimData);

    const hasAnyNotes = useMemo(() => {
        return serviceSummary.some(service => service.notes && service.notes !== "-");
    }, [serviceSummary]);

    const ROW_LABELS: ReadonlyArray<RowLabel> = useMemo(() => {
        const labels: RowLabel[] = [
            { label: t("claimsSummary.amountCharged"), key: "charged" },
            { label: t("claimsSummary.amountNotCovered"), key: "amountNotCovered" },
            { label: t("claimsSummary.amountAllowed"), key: "amountAllowed" },
            { label: t("claimsSummary.deductible_amount"), key: "deductible" },
            { label: t("claimsSummary.copayCoinsurance"), key: "copayCoinsurance" },
            { label: t("claimsSummary.amountPaidByPlan"), key: "paidByPlan" },
            { label: t("claimsSummary.patientResponsibility"), key: "patientResponsibility" },
        ];

        if (hasAnyNotes) {
            labels.push({ label: t("claimsSummary.note"), key: "notes" });
        }

        return labels;
    }, [t, hasAnyNotes]);

    const rows = useMemo(() => {
        return ROW_LABELS.map((row) => {
            const cells = [
                {
                    text: <th scope="row">{row.label}</th>,
                },
            ];

            const serviceCells = serviceSummary.map((service, index) => {
                const value = service[row.key];
                
                if (row.key === 'notes' && service.notes && service.noteDisplay) {
                    return {
                        text: (
                            <Tooltip
                                content={service.noteDisplay}
                                placement="top"
                                showDelay={250}
                                hideDelay={400}
                                id="button_tooltip"
                            >
                                <span className="ws-nowrap text-decoration-underline">
                                    {service.notes}
                                </span>
                            </Tooltip>
                        )
                    };
                }
                
                return {
                    text: formatCellValue(row.key, value)
                };
            });

            return [...cells, ...serviceCells];
        });
    }, [serviceSummary, ROW_LABELS]);

    const header = useMemo(() => {
        const header = {
            text: `${t("proxyApproval.details")}`
        };

        const serviceHeaders = serviceSummary.map((service) => ({
            text: formatMedicalText(service.serviceType),
        }));

        return [header, ...serviceHeaders];
    }, [serviceSummary, t]);

    return (
        <div className="print">
            <Headings
                level={3}
                text={t("claimsSummary.typeofService")}
                variant="semiBold"
                className="mb-4"
            />
            {isMobile ? (
                <Paragraph
                    text={t("claimsSummary.scrollInstructions")}
                    className="mb-3"
                />
            ) : null}
            <Table
                header={header}
                rows={rows}
                className="fixed-column-table"
                headerBgColor="secondary"
                headerTextColor="primary"
            />
        </div>
    );
};

export default ClaimsServiceTypeTable;
